/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppLayout } from '@views/layouts/AppLayout/AppLayout';
import AuthLayout from '@views/layouts/AuthLayout/AuthLayout';
import { RequestResetPassword } from '@views/pages/Auth/ResetPassword/RequestFlow/RequestResetPassword';
import { Register } from '@views/pages/Auth/Register';
import { Navigate, Outlet, redirect } from 'react-router-dom';
import { MFA } from '@views/pages/Auth/MFA';
import { ResetPassword } from '@views/pages/Auth/ResetPassword/ConfirmFlow';
import { AdminPanel } from '@views/pages/Admin';
import { Dashboard } from '@views/pages/Dashboard/Dashboard';
import { Organization } from '@views/pages/Admin/Organization';
import { PanelLanding } from '@views/pages/Admin/PanelLanding';
import { User } from '@views/pages/Admin/User';
import { Team } from '@views/pages/Admin/Teams';
import { Partner } from '@views/pages/Admin/Partner';
import { Practice } from '@views/pages/Admin/Practice';
import { Operator } from '@views/pages/Admin/Operator';
import { Building } from '@views/pages/Admin/Buildings';
import { Provider } from '@views/pages/Admin/Provider';
import { Login } from '@views/pages/Auth';
import { LoginMFA } from '@views/pages/Auth/Login/LoginMFA';
import { LoginStepEmail } from '@views/pages/Auth/Login/LoginStepEmail';
import { Profile } from '@views/pages/Profile';
import { NotFound } from '@views/pages/NotFound';
import { AccessDenied } from '@views/pages/AccessDenied';
import { SomethingWentWrong } from '@views/pages/SomethingWentWrong';
import { ServerError } from '@views/pages/ServerError';
import { Patients } from '@views/pages/Patients';
import { Patient } from '@views/pages/Patient';
import { MyWorklist } from '@views/pages/Worklist/MyWorklist/MyWorklist';
import { AllWorklist } from '@views/pages/Worklist';
import { AssessmentPage } from '@views/pages/ExternalPages';
import { PatientContextProvider } from '@views/pages/Patient/context';
import { ReportPage } from '@views/pages/Reporting/Reports';
import { PreviewWrapper } from '@views/pages/Reporting/Reports/Report/components/Preview/PreviewWrapper';
import { ReportFolders } from 'views/pages/Reporting/ReportFolders';
import { Reporting } from '@views/pages/Reporting/Reports/Reporting.tsx';
import { ReportItemsView } from '@views/pages/ReportItemsView';
import { Downloads } from '@views/pages/Downloads';
import { ActivityLogs } from '@views/pages/ActivityLogs';
import { ReportsFolderPreview } from '@views/pages/Reporting/ReportsFolderPreview';
import { DataAnalysis } from '@views/pages/DataAnalysis';
import { LandingPage } from '@views/pages/LandingPage';
import { RouteError } from 'components/Error';
import ProtectedRoute from 'components/ProtectedRoute/ProtectedRoute';
import { TimeOff } from '@views/pages/TimeOff';

const router = [
  {
    path: '/',
    errorElement: <RouteError />,
    children: [
      {
        path: 'perform-assessment/:assessment_id/:token',
        loader: ({ params }: any) =>
          redirect(
            `/perform-assessment/${params.assessment_id}?t=${params.token}`,
          ),
      },
      {
        path: 'perform-assessment/:assessment_id',
        element: <AssessmentPage />,
      },
      {
        path: '/',
        // This is done in order to avoid a flash of the dashboard on
        // screen, before routing to auth
        loader: () => {
          const token = localStorage.getItem('token');
          if (!token) {
            return redirect('/auth/login');
          }
          return;
        },
        element: <AppLayout />,
        ErrorBoundary: Dashboard,
        children: [
          { path: '/', element: <LandingPage /> },
          {
            path: 'patient',
            element: <Outlet />,
            children: [
              { index: true, element: <Navigate to='list' replace /> },
              {
                path: 'list',
                element: <Outlet />,
                children: [
                  { index: true, element: <Navigate to='recent' replace /> },
                  { path: ':tab', element: <Patients /> },
                ],
              },
              {
                path: ':patient_id',
                element: (
                  <PatientContextProvider>
                    <Patient />
                  </PatientContextProvider>
                ),
              },
            ],
          },
          {
            path: 'admin',
            element: <ProtectedRoute permissions={['view_admin_panel']} />,
            children: [
              {
                element: <AdminPanel />,
                children: [
                  { index: true, element: <PanelLanding /> },
                  { path: 'organization', element: <Organization /> },
                  { path: 'user', element: <User /> },
                  { path: 'team', element: <Team /> },
                  { path: 'partner', element: <Partner /> },
                  { path: 'practice', element: <Practice /> },
                  { path: 'operator', element: <Operator /> },
                  { path: 'building', element: <Building /> },
                  { path: 'provider', element: <Provider /> },
                ],
              },
            ],
          },
          {
            path: 'dashboard',
            element: <Dashboard />,
          },
          {
            path: 'worklist',
            children: [
              { index: true, element: <Navigate to='mine' replace /> },
              { path: 'all', element: <AllWorklist /> },
              { path: 'mine', element: <MyWorklist /> },
            ],
          },
          {
            path: 'profile',
            element: <Profile />,
          },
          {
            path: 'data-analysis',
            element: <DataAnalysis />,
          },
          {
            path: 'downloads',
            element: <Downloads />,
          },
          {
            path: '/activity-logs',
            element: <ActivityLogs />,
          },
          {
            path: '/time-off',
            element: <TimeOff />,
          },
          {
            path: 'reporting',
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <Navigate to='reports' replace />,
              },
              {
                path: 'create',
                element: <ReportPage />,
              },
              {
                path: 'edit/:report_id',
                element: <ReportPage />,
              },
              {
                path: 'preview',
                element: <PreviewWrapper />,
              },
              {
                path: 'items/:report_id',
                element: <ReportItemsView />,
              },
              {
                path: 'folders',
                element: <ReportFolders />,
              },
              {
                path: 'reports',
                element: <Reporting />,
              },
              {
                path: 'reports-folder-preview/:folder_id',
                element: <ReportsFolderPreview />,
              },
            ],
          },
        ],
      },
      {
        path: 'auth',
        element: <AuthLayout />,
        children: [
          {
            path: 'register/password',
            element: <Register />,
          },
          {
            path: 'login/mfa',
            element: <LoginMFA />,
          },
          {
            path: 'login/email',
            element: <LoginStepEmail />,
          },
          {
            path: 'login',
            element: <Login />,
          },
          {
            path: 'password-reset',
            element: <ResetPassword />,
          },
          {
            path: 'request-password-reset',
            element: <RequestResetPassword />,
          },
          {
            path: 'mfa',
            element: <MFA />,
          },
        ],
      },
    ],
  },
  {
    path: '/access-denied',
    element: <AccessDenied />,
  },
  {
    path: '/something-went-wrong',
    element: <SomethingWentWrong />,
  },
  {
    path: '/server-error',
    element: <ServerError />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default router;
